import { Navigate, Route, Routes, useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { LoginPage } from "./pages/auth/login";
import { RegisterPage } from "./pages/auth/register";
import { TfaConfirmPage } from "./pages/auth/tfa.confirm";
import { VerifyEmailPage } from "./pages/auth/verify.email";
import { Header } from "./pages/header/header";
import { ProfilePage } from "./pages/profile/profile";
import { PrivateRoutes } from "./components/shared/private.route";
import { Web3ContextProvider } from "./providers/web3.context";
import { ForgotPassword } from "./pages/auth/forgot.password";
import { ConfirmResetPasswordPage } from "./pages/auth/confirm.reset.password";
import { ResetPasswordConfirmationPage } from "./pages/auth/reset.password.confirmation";
import { AccountCreationConfirmationPage } from "./pages/auth/account.creation.confirmation";
import { MuseumSpace } from "./pages/exhibition/museum.space";
import { WebGLContextProvider } from "./providers/WebGL.context";
import { MintUnavailablePage } from "./pages/nfts/mint.unavailable";
import { NFTListPage } from "./pages/nfts/list";
import { GetVouchers } from "./pages/nfts/store/nfts.actions";

import "./styles/App.scss";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { lazy, Suspense, useEffect } from "react";
import { UserList } from "./pages/admin/users";
import { InstitutionsPage } from "./pages/admin/institutions";
import { System } from "./pages/admin/system";
import { GallerySpace } from "./pages/exhibition/gallery.space";
import { AdminEdmRoutes, AdminRoutes, StrictAdminRoutes } from "./components/shared/admin.route";
import { GalleriesList } from "./pages/admin/galleries/galleries";
import { EditGallery } from "./pages/admin/galleries/edit.gallery";
import { NewGallery } from "./pages/admin/galleries/new.gallery";
import { DBBackupPage } from "./pages/admin/dbbackup";
import { useDispatch } from "react-redux";
import { PersonalSpace } from "./pages/exhibition/personal.space";
import { GallerySetup } from "./pages/exhibition/gallery.setup";
import { Galleries } from "./pages/exhibition/galleries";
import { useAutoLogin } from "./hooks/useAutoLogin";
import { useSelector } from "react-redux";
import { AppState } from "./store/root.reducer";
import { GalleryFiles } from "./pages/admin/files/gallery";
import { ArtworkFiles } from "./pages/admin/files/artwork";
import { AdminMoMComponent } from "./components/3d/admin-mom";
import { AssignMoods } from "./pages/admin/assign-moods";
import { InstructionsProvider } from "./contexts/InstructionsContext";
import { FTUEProvider } from "./contexts/FTUEContext";
import { GalleryChromeProvider } from "./contexts/GalleryChromeContext";
import { FTUEContainer } from "./components/ftue/FTUEContainer";
import { Feedbacks } from "./pages/admin/feedback";
import { GalleryLabelsPage } from "./pages/admin/gallery-labels";
import { PreviewSpace } from "./pages/exhibition/preview.space";
import TermsAndConditions from "./pages/terms-policy/terms";
import PrivacyPolicy from "./pages/terms-policy/privacy-policy";
import { ArtworkEmbedPage } from "./pages/embed/artwork.embed";
import { AdminEdmDatasetDetailPage } from "./pages/admin/edm/dataset.detail";
import { ArtworkWorkspacePage } from "./pages/artworks/workspace";
import { ArtworkWorkspaceDetailPage } from "./pages/artworks/workspace.detail";
import { configureAuthSessionRecovery } from "./utils/authSession";
import { axiosInstance } from "./utils/api";
import { trackPageView } from "./utils/telemetry";
import { PlatformPage } from "./pages/platform/platform";
import { PlatformMigrationVerifyPage } from "./pages/platform/platform-migration-verify";

const SpatialMigrationsPage = lazy(() => import("./pages/admin/spatial-migrations"));
const SpatialMigrationWorkspacePage = lazy(() => import("./pages/admin/spatial-migration-workspace"));
const AnalyticsPage = lazy(() => import("./pages/admin/analytics"));

const ApplicationLoadingState = () => (
  <div id="loading" className="loading-screen" role="status" aria-live="polite">
    Loading ...
  </div>
);

const LegacyArtworkDetailRedirect = () => {
  const { artworkId } = useParams();
  return <Navigate to={artworkId ? `/artwork/list/${artworkId}` : "/artwork/list"} replace />;
};

function App() {
  const dispatch = useDispatch();
  const autoLogin = useAutoLogin();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const location = useLocation();
  const mode = searchParams.get("mode");
  const { user } = useSelector((state: AppState) => state.auth);

  useEffect(() => {
    const status = process.env.REACT_APP_STATUS;
    const title = process.env.REACT_APP_NAME;
    document.title = status === "production" ? title : `${title} - ${status}`;
  }, []);

  useEffect(() => {
    const headerHeight = mode === 'iframe' ? '0px' : '72px';
    document.documentElement.style.setProperty('--header-height', headerHeight);
  }, [mode]);

  useEffect(() => {
    configureAuthSessionRecovery({
      clearAuthHeaders: () => {
        delete axios.defaults.headers.common["Authorization"];
        delete axiosInstance.defaults.headers.common["Authorization"];
      },
      dispatch,
      navigate,
      isAuthenticated: Boolean(user),
    });
  }, [dispatch, navigate, user]);

  useEffect(() => {
    if (!user) {
      autoLogin(false);
    }
  }, [user]);

  useEffect(() => {
    const path = `${location.pathname}${location.search || ""}`;
    trackPageView(path, mode);
  }, [location.pathname, location.search, mode]);

  const pathname = location.pathname;
  const isEmbedRoute = pathname.startsWith("/embed/artwork/");
  const isGalleryRoute =
    pathname === "/" ||
    pathname.startsWith("/exhibitions/") ||
    pathname.startsWith("/gallery/") ||
    pathname.startsWith("/personal/mom") ||
    pathname.startsWith("/preview-gallery") ||
    pathname.startsWith("/admin/mom") ||
    isEmbedRoute;

  return (
    <div className="App">
      <WebGLContextProvider>
        <Web3ContextProvider>
          <InstructionsProvider>
            <FTUEProvider>
            <GalleryChromeProvider>
          {/* Control header visibility by mode, and set CSS var for WebGL layout */}
          <div style={{
            ['--header-height' as any]: (mode === 'iframe' || isEmbedRoute) ? '0px' : '72px'
          }}>
            {(mode === "iframe" || isEmbedRoute) ? null : <Header />}
          </div>
          <div className={isGalleryRoute ? "" : "margin-content-top"}>
            <Routes>
              <Route path="/" element={<MuseumSpace />} />
              <Route path="/embed/artwork/:id" element={<ArtworkEmbedPage />} />
              <Route path="/platform" element={<PlatformPage />} />
              <Route path="/platform/verify-migration" element={<PlatformMigrationVerifyPage />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/auth/register" element={<RegisterPage />} />
              <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
              <Route path="/auth/login-tfa" element={<TfaConfirmPage />} />
              <Route path="/auth/login" element={<LoginPage />} />
              <Route path="/auth/reset-password" element={<ForgotPassword />} />
              <Route
                path="/auth/confirm-reset-password"
                element={<ConfirmResetPasswordPage />}
              />
              <Route
                path="/auth/reset-password-success"
                element={<ResetPasswordConfirmationPage />}
              />
              <Route
                path="/auth/account-created"
                element={<AccountCreationConfirmationPage />}
              />
              <Route path="/gallery/:id" element={<GallerySpace />} />
              <Route path="/exhibitions/:space" element={<MuseumSpace />} />
              <Route path="/preview-gallery" element={<PreviewSpace />} />
              <Route element={<PrivateRoutes />}>
                <Route path="/personal/mom" element={<PersonalSpace />} />
                <Route path="/nft" element={<NFTListPage />} />
                <Route path="/nft/mint" element={<MintUnavailablePage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route
                  path="/artwork/create-artwork"
                  element={<Navigate to="/artwork/list?tab=add-artwork&addMode=single" replace />}
                />
                <Route path="/artwork/batch" element={<Navigate to="/artwork/list?tab=add-artwork&addMode=multi" replace />} />
                <Route path="/artwork/list" element={<ArtworkWorkspacePage />} />
                <Route path="/artwork/list/new" element={<ArtworkWorkspaceDetailPage />} />
                <Route path="/artwork/list/:artworkId" element={<ArtworkWorkspaceDetailPage />} />
                {/* <Route path="/gallerysetup" element={<Galleries />} /> */}
                {/* <Route path="/gallerysetup/:galleryId" element={<GallerySetup />} /> */}
                <Route element={<AdminRoutes />}>
                  <Route path="/admin/mom" element={<AdminMoMComponent />} />
                  <Route path="/admin/users" element={<UserList />} />
                  <Route path="/admin/institutions" element={<InstitutionsPage />} />
                  <Route path="/admin/system" element={<System />} />
                  <Route path="/admin/galleries" element={<GalleriesList />} />
                  <Route
                    path="/admin/galleries/edit/:galleryId"
                    element={<EditGallery />}
                  />
                  <Route
                    path="/admin/galleries/edit/"
                    element={<EditGallery />}
                  />
                  <Route path="/admin/galleries/new/" element={<NewGallery />} />
                  <Route path="/admin/artworks" element={<Navigate to="/artwork/list?tab=artworks" replace />} />
                  <Route path="/admin/artworks/edit/" element={<Navigate to="/artwork/list/new" replace />} />
                  <Route
                    path="/admin/artworks/edit/:artworkId"
                    element={<LegacyArtworkDetailRedirect />}
                  />
                  <Route path="/admin/files/gallery" element={<GalleryFiles />} />
                  <Route path="/admin/files/artwork" element={<ArtworkFiles />} />
                  <Route path="/admin/assign-moods" element={<AssignMoods />} />
                  <Route path="/admin/feedbacks" element={<Feedbacks />} />
                  <Route path="/admin/gallery-labels" element={<GalleryLabelsPage />} />
                </Route>
                <Route element={<StrictAdminRoutes />}>
                  <Route
                    path="/admin/spatial-migrations"
                    element={(
                      <Suspense fallback={<ApplicationLoadingState />}>
                        <SpatialMigrationsPage />
                      </Suspense>
                    )}
                  />
                  <Route
                    path="/admin/spatial-migrations/:runId"
                    element={(
                      <Suspense fallback={<ApplicationLoadingState />}>
                        <SpatialMigrationWorkspacePage />
                      </Suspense>
                    )}
                  />
                  <Route
                    path="/admin/analytics"
                    element={(
                      <Suspense fallback={<ApplicationLoadingState />}>
                        <AnalyticsPage />
                      </Suspense>
                    )}
                  />
                  <Route path="/admin/backups" element={<DBBackupPage />} />
                </Route>
                <Route element={<AdminEdmRoutes />}>
                  <Route path="/admin/edm/datasets" element={<Navigate to="/artwork/list?tab=datasets" replace />} />
                  <Route path="/admin/edm/datasets/:datasetKey" element={<AdminEdmDatasetDetailPage />} />
                  <Route path="/admin/edm/artworks" element={<Navigate to="/artwork/list?tab=artworks" replace />} />
                  <Route path="/admin/edm/artworks/:artworkId" element={<LegacyArtworkDetailRedirect />} />
                </Route>
              </Route>
              </Routes>
          </div>
            {/* FTUE Container - handles the overlay and highlighting */}
            <FTUEContainer />
            </GalleryChromeProvider>
            </FTUEProvider>
          </InstructionsProvider>
        </Web3ContextProvider>
      </WebGLContextProvider>
    </div>
  );
}

export default App;
